// WP-07 — client-side tenant resolution (D-MT-1, PLAT-MT-1.0 §4.2; LLD-05
// §2.6). The subdomain/slug is a ROUTING HINT ONLY: the server authority is
// always the JWT `tid` claim. On `{slug}.steier.co.za` the sign-in card goes
// straight to credentials (the API resolves the tenant from the Host header);
// on the bare domain the SPA asks for a company code first — never an
// email-based tenant picker (no tenant enumeration).

/** Labels never issued as tenant slugs (Caddyfile reserved hosts). */
const RESERVED_LABELS = new Set(["api", "app", "www", "staging", "grafana"]);

const DEFAULT_PLATFORM_DOMAIN = "steier.co.za";

export type TenantResolution =
  { kind: "subdomain"; slug: string } | { kind: "bare" };

export function platformDomain(): string {
  // Baked at Vite build time; the same build serves every tenant (MT-F26).
  return import.meta.env.VITE_PLATFORM_DOMAIN ?? DEFAULT_PLATFORM_DOMAIN;
}

/**
 * Resolve the tenant hint from a hostname. Anything that is not a plain
 * `{slug}.{platformDomain}` tenant host (bare domain, reserved labels,
 * localhost/IP dev hosts, unknown domains) falls back to company-code entry.
 */
export function resolveTenant(
  hostname: string,
  domain: string = platformDomain(),
): TenantResolution {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  if (host === domain || host === `app.${domain}`) return { kind: "bare" };
  if (!host.endsWith(`.${domain}`)) return { kind: "bare" };

  const label = host.slice(0, -(domain.length + 1));
  // Nested labels (a.b.steier.co.za) and reserved hosts are not tenant slugs.
  if (label.includes(".") || RESERVED_LABELS.has(label) || label.length === 0) {
    return { kind: "bare" };
  }
  return { kind: "subdomain", slug: label };
}

/** Tenant hint for the current browser location. */
export function resolveCurrentTenant(): TenantResolution {
  return resolveTenant(window.location.hostname);
}
